import type { CheckoutOrderResult } from '@/lib/api'

const CHECKOUT_SUCCESS_STORAGE_VERSION = 1
const CHECKOUT_SUCCESS_STORAGE_KEY_PREFIX = `checkout-success-v${CHECKOUT_SUCCESS_STORAGE_VERSION}`
const CHECKOUT_SUCCESS_STORAGE_GLOBAL_KEY = `${CHECKOUT_SUCCESS_STORAGE_KEY_PREFIX}:last`
const CHECKOUT_SUCCESS_STORAGE_ANONYMOUS_KEY = `${CHECKOUT_SUCCESS_STORAGE_KEY_PREFIX}:anonymous`
const CHECKOUT_SUCCESS_STORAGE_TTL_MS = 1000 * 60 * 60 * 12

const normalizeCheckoutStorageScope = (value?: string | null) => {
  const normalized = String(value ?? '').trim().replace(/[^a-zA-Z0-9:_-]+/g, '-')
  return normalized || null
}

type PersistedCheckoutSuccess = {
  version: number
  updatedAt: number
  result: CheckoutOrderResult
}

const sanitizePersistedCheckoutSnapshot = (
  value: unknown
): CheckoutOrderResult['checkoutSnapshot'] => {
  if (!value || typeof value !== 'object') return null

  const source = value as Partial<NonNullable<CheckoutOrderResult['checkoutSnapshot']>>

  const subtotal = typeof source.subtotal === 'number' && Number.isFinite(source.subtotal)
    ? Math.max(0, source.subtotal)
    : null
  const desconto = typeof source.desconto === 'number' && Number.isFinite(source.desconto)
    ? Math.max(0, source.desconto)
    : null
  const frete = typeof source.frete === 'number' && Number.isFinite(source.frete)
    ? Math.max(0, source.frete)
    : null
  const impostos = typeof source.impostos === 'number' && Number.isFinite(source.impostos)
    ? Math.max(0, source.impostos)
    : null
  const total = typeof source.total === 'number' && Number.isFinite(source.total)
    ? Math.max(0, source.total)
    : null

  if (
    subtotal === null
    || desconto === null
    || frete === null
    || impostos === null
    || total === null
  ) {
    return null
  }

  const freteSelecionadoSource = source.freteSelecionado && typeof source.freteSelecionado === 'object'
    ? source.freteSelecionado
    : null

  return {
    subtotal,
    desconto,
    frete,
    impostos,
    total,
    freteSelecionado: freteSelecionadoSource
      ? {
          transportadoraId: typeof freteSelecionadoSource.transportadoraId === 'number' && Number.isFinite(freteSelecionadoSource.transportadoraId)
            ? Math.max(0, Math.trunc(freteSelecionadoSource.transportadoraId))
            : 0,
          cotacaoId: typeof freteSelecionadoSource.cotacaoId === 'number' && Number.isFinite(freteSelecionadoSource.cotacaoId)
            ? Math.max(0, Math.trunc(freteSelecionadoSource.cotacaoId))
            : 0,
          tipoFrete: freteSelecionadoSource.tipoFrete === 'fob' ? 'fob' : 'cif',
          fretePorConta: freteSelecionadoSource.fretePorConta === 1 ? 1 : 0,
          valorFrete: typeof freteSelecionadoSource.valorFrete === 'number' && Number.isFinite(freteSelecionadoSource.valorFrete)
            ? Math.max(0, freteSelecionadoSource.valorFrete)
            : 0,
          valorOriginal: typeof freteSelecionadoSource.valorOriginal === 'number' && Number.isFinite(freteSelecionadoSource.valorOriginal)
            ? Math.max(0, freteSelecionadoSource.valorOriginal)
            : 0,
        }
      : null,
  }
}

const sanitizePersistedStatusFluxo = (value: unknown): CheckoutOrderResult['statusFluxo'] => {
  if (typeof value !== 'string') return 'em_analise'

  if (
    value === 'aguardando_pagamento'
    || value === 'aguardando_confirmacao'
    || value === 'em_separacao'
    || value === 'aprovado'
  ) {
    return value
  }

  return 'em_analise'
}

const sanitizePersistedCheckoutSuccessResult = (value: unknown): CheckoutOrderResult | null => {
  if (!value || typeof value !== 'object') return null

  const source = value as Partial<CheckoutOrderResult>
  const pedidoId = typeof source.pedidoId === 'number' && Number.isFinite(source.pedidoId)
    ? Math.trunc(source.pedidoId)
    : 0

  if (pedidoId <= 0) return null

  const numeroPedido = typeof source.numeroPedido === 'number' && Number.isFinite(source.numeroPedido)
    ? Math.trunc(source.numeroPedido)
    : 0

  const paymentMethodSource = source.paymentMethod && typeof source.paymentMethod === 'object'
    ? source.paymentMethod as NonNullable<CheckoutOrderResult['paymentMethod']>
    : null

  return {
    pedidoId,
    numeroPedido,
    hash: typeof source.hash === 'string' ? source.hash : '',
    statusFluxo: sanitizePersistedStatusFluxo(source.statusFluxo),
    statusNumero: typeof source.statusNumero === 'number' && Number.isFinite(source.statusNumero)
      ? Math.trunc(source.statusNumero)
      : null,
    statusDescricao: typeof source.statusDescricao === 'string' ? source.statusDescricao : '',
    itensProcessados: typeof source.itensProcessados === 'number' && Number.isFinite(source.itensProcessados)
      ? Math.trunc(source.itensProcessados)
      : 0,
    paymentMethod: paymentMethodSource
      ? {
          formaPagamentoId: typeof paymentMethodSource.formaPagamentoId === 'number' && Number.isFinite(paymentMethodSource.formaPagamentoId)
            ? Math.trunc(paymentMethodSource.formaPagamentoId)
            : 0,
          condicaoPagamentoId: typeof paymentMethodSource.condicaoPagamentoId === 'number' && Number.isFinite(paymentMethodSource.condicaoPagamentoId)
            ? Math.trunc(paymentMethodSource.condicaoPagamentoId)
            : 0,
          label: typeof paymentMethodSource.label === 'string' ? paymentMethodSource.label : '',
          description: typeof paymentMethodSource.description === 'string' ? paymentMethodSource.description : '',
          detail: typeof paymentMethodSource.detail === 'string' ? paymentMethodSource.detail : '',
        }
      : null,
    checkoutSnapshot: sanitizePersistedCheckoutSnapshot(source.checkoutSnapshot),
    paymentInstructions: source.paymentInstructions ?? null,
    paymentLink: typeof source.paymentLink === 'string' ? source.paymentLink : null,
  }
}

const readPersistedCheckoutSuccessFromStorage = (
  storage: Storage,
  storageKey: string
): CheckoutOrderResult | null => {
  try {
    const rawValue = storage.getItem(storageKey)
    if (!rawValue) return null

    const parsed = JSON.parse(rawValue) as Partial<PersistedCheckoutSuccess>
    const updatedAt = typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0
    if (!updatedAt || Date.now() - updatedAt > CHECKOUT_SUCCESS_STORAGE_TTL_MS) {
      storage.removeItem(storageKey)
      return null
    }

    return sanitizePersistedCheckoutSuccessResult(parsed.result)
  } catch (error) {
    console.warn('Failed to read checkout success from storage:', error)
    return null
  }
}

export const buildCheckoutSuccessStorageKeys = (
  authenticatedEntidadeId?: number | null,
  storageScope?: string | null
) => {
  const scopedKey = authenticatedEntidadeId && authenticatedEntidadeId > 0
    ? `${CHECKOUT_SUCCESS_STORAGE_KEY_PREFIX}:${Math.trunc(authenticatedEntidadeId)}`
    : CHECKOUT_SUCCESS_STORAGE_ANONYMOUS_KEY
  const normalizedScope = normalizeCheckoutStorageScope(storageScope)
  const baseKeys = [
    scopedKey,
    CHECKOUT_SUCCESS_STORAGE_GLOBAL_KEY,
    CHECKOUT_SUCCESS_STORAGE_ANONYMOUS_KEY,
  ]

  return Array.from(new Set(
    baseKeys.map((key) => (normalizedScope ? `${key}:${normalizedScope}` : key))
  ))
}

export const readPersistedCheckoutSuccess = (storageKeys: string[]): CheckoutOrderResult | null => {
  if (typeof window === 'undefined') return null

  for (const storageKey of storageKeys) {
    const fromSession = readPersistedCheckoutSuccessFromStorage(window.sessionStorage, storageKey)
    if (fromSession) return fromSession

    const fromLocal = readPersistedCheckoutSuccessFromStorage(window.localStorage, storageKey)
    if (fromLocal) return fromLocal
  }

  return null
}

export const writePersistedCheckoutSuccess = (storageKeys: string[], result: CheckoutOrderResult) => {
  if (typeof window === 'undefined') return false

  const payload: PersistedCheckoutSuccess = {
    version: CHECKOUT_SUCCESS_STORAGE_VERSION,
    updatedAt: Date.now(),
    result,
  }
  const serialized = JSON.stringify(payload)
  let hasPersisted = false

  storageKeys.forEach((storageKey) => {
    try {
      window.sessionStorage.setItem(storageKey, serialized)
      hasPersisted = true
    } catch (error) {
      console.warn('Failed to persist checkout success to sessionStorage:', error)
    }

    try {
      window.localStorage.setItem(storageKey, serialized)
      hasPersisted = true
    } catch (error) {
      console.warn('Failed to persist checkout success to localStorage:', error)
    }
  })

  return hasPersisted
}

export const clearPersistedCheckoutSuccess = (storageKeys: string[]) => {
  if (typeof window === 'undefined') return

  storageKeys.forEach((storageKey) => {
    try {
      window.sessionStorage.removeItem(storageKey)
    } catch (error) {
      console.warn('Failed to clear checkout success from sessionStorage:', error)
    }

    try {
      window.localStorage.removeItem(storageKey)
    } catch (error) {
      console.warn('Failed to clear checkout success from localStorage:', error)
    }
  })
}
